/* eslint-disable complexity */
import { Injectable } from '@angular/core';
import { NavigationEnd, Router, Event } from '@angular/router';
import { BehaviorSubject, filter } from 'rxjs';
import { SequenceRequest } from '@sequence/models/request/sequence.request';
import { FlightParams } from '../models/flight-params';

/**
 * This service handles triggering events based on route changes. It uses router events
 * instead of paramMap to be able to detect global url changes. It also exposes the current
 * sequence and/or flight parameters.
 */
@Injectable({
  providedIn: 'root',
})
export class RouteParameterStore {
  /** The current screen. Used for breadcrumb navigation. */
  private _currentScreen = new BehaviorSubject<
    'calendar' | 'sequence' | 'flight' | 'search' | null
  >(null);
  currentScreen$ = this._currentScreen.asObservable();

  // Sequence params captured from route.
  private _sequenceParams = new BehaviorSubject<SequenceRequest | null>(null);
  sequenceParams$ = this._sequenceParams.asObservable();

  // Flight params captured from route. Includes associated sequence params.
  private _flightParams = new BehaviorSubject<FlightParams | null>(null);
  flightParams$ = this._flightParams.asObservable();

  private _calendarEmployeeNumber = new BehaviorSubject<string | null>(null);
  calendarEmployeeNumber$ = this._calendarEmployeeNumber.asObservable();

  constructor(private router: Router) {
    // Activated route params updates don't trigger within this service, so general
    // router events are captured instead.
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: Event) => {
        this.handleRouterEvent(event);
      });
  }

  /** The current sequence parameters from the route. */
  get currentSequenceParams(): SequenceRequest | null {
    return this._sequenceParams.getValue();
  }

  /** The current sequence and flight parameters from the route. */
  get currentFlightParams(): FlightParams | null {
    return this._flightParams.getValue();
  }

  get calendarEmployeeNumber(): string | null {
    return this._calendarEmployeeNumber.getValue();
  }

  private handleRouterEvent(event: Event): void {
    // This will always be true due to filter.
    if (event instanceof NavigationEnd) {
      const params = event.urlAfterRedirects.split('/');

      // Clear sequence and flight params.
      this._sequenceParams.next(null);
      this._flightParams.next(null);
      this._calendarEmployeeNumber.next(null);

      // Handle calendar.
      if (params.length > 0 && params[1] === '') {
        this._currentScreen.next('calendar');
      }

      // Handle calendar admin mode (specified employee number)
      if (params.length === 3 && params[1] === 'calendar') {
        this._calendarEmployeeNumber.next(params[2]);
        this._currentScreen.next('calendar');
      } else if (params.length === 2 && params[1] === 'search') {
        this._currentScreen.next('search');
      }

      // Handle sequence.
      else if (params.length === 5 && params[1] === 'sequence') {
        this.handleSequenceEvent(params);
      }

      // Handle flight.
      else if (params.length === 10 && params[5] === 'flight') {
        this.handleFlightEvent(params);
      }

      // Default.
      else {
        this._currentScreen.next('calendar');
      }
    }
  }

  /** Save sequence URL and extract params for sequence request. */
  private handleSequenceEvent(params: string[]): void {
    this._currentScreen.next('sequence');

    if (params[2] !== '' && params[3] !== '' && params[4] !== '') {
      // Update sequence params observable. This will lead to a sequence API request.
      this._sequenceParams.next(this.mapSequenceParams(params));
    }
  }

  /** Convert param array into sequence request (sequence params). */
  private mapSequenceParams(params: string[]): SequenceRequest {
    return {
      number: params[2],
      seat: params[3],
      date: params[4],
    };
  }

  /** Extract flight params from route params. */
  private mapFlightParams(params: string[]): FlightParams {
    return {
      sequenceParams: this.mapSequenceParams(params),
      flightNumber: params[6],
      depStation: params[7],
      arrStation: params[8],
      depDate: params[9],
    };
  }

  /**
   * If flight data isn't present, use sequence request to fetch sequence and lookup
   * the specified flight.
   */
  private handleFlightEvent(params: string[]): void {
    this._currentScreen.next('flight');

    const sequenceRequest = this.mapSequenceParams(params);
    this._sequenceParams.next(sequenceRequest);

    const flightParams = this.mapFlightParams(params);
    this._flightParams.next(flightParams);
  }
}
